import clsx from "clsx";
import React, { forwardRef, useCallback, useEffect, useRef } from "react";

import sharedStyles from "../../styles/shared/inputs.module.css";
import styles from "./index.module.css";

interface TextAreaProps extends React.ComponentProps<"textarea"> {
  /** If input has an error */
  hasError?: boolean;
  /** If input is disabled */
  isDisabled?: boolean;
  /** Value of text input */
  value?: string;
  /** Footer - should be TextAreaFooter component */
  children?: React.ReactElement<TextAreaFooterProps>;
  /** ID(s) of elements that describe this textarea (for error messages, help text, etc.) */
  "aria-describedby"?: string;
  /** Indicates if the textarea has a validation error */
  "aria-invalid"?: boolean;
  /** Indicates if the textarea is required */
  "aria-required"?: boolean;
  /** Label for screen readers when a visible label is not present */
  "aria-label"?: string;
}

/**
 * Uses `textarea` input to display multi-line text.
 */

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (props, ref) => {
    const {
      className,
      value,
      hasError = false,
      isDisabled = false,
      children,
      "aria-describedby": ariaDescribedby,
      "aria-invalid": ariaInvalid,
      "aria-required": ariaRequired,
      "aria-label": ariaLabel,
      ...rest
    } = props;
    const internalRef = useRef<HTMLTextAreaElement>(null);
    const textareaRef = ref || internalRef;

    const adjustHeight = useCallback(() => {
      const textarea =
        textareaRef && "current" in textareaRef ? textareaRef.current : null;
      if (!textarea) return;

      textarea.style.height = "1px";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }, [textareaRef]);

    useEffect(() => {
      adjustHeight();
    }, [value, adjustHeight]);

    const handleInput = useCallback(
      (e: React.FormEvent<HTMLTextAreaElement>) => {
        adjustHeight();
        if (rest.onInput) {
          rest.onInput(e);
        }
      },
      [adjustHeight, rest]
    );

    const classes = clsx(
      styles.root,
      {
        [styles.hasError]: hasError,
        [styles.hasFooter]: children,
        [sharedStyles.isDisabled]: isDisabled,
      },
      className
    );

    return (
      <div className={classes}>
        <textarea
          ref={textareaRef}
          value={value}
          disabled={isDisabled}
          className={styles.input}
          onInput={handleInput}
          aria-describedby={ariaDescribedby}
          aria-invalid={ariaInvalid ?? hasError}
          aria-required={ariaRequired}
          aria-label={ariaLabel}
          {...rest}
        />
        {children}
      </div>
    );
  }
);

/**
 * Footer for various buttons.
 */

interface TextAreaFooterProps extends React.ComponentProps<"div"> {
  /** Footer content (buttons, text, etc.) */
  children: React.ReactNode;
  /** Horizontal alignment of footer content */
  justify?: "start" | "end" | "between";
}

const TextAreaFooter: React.FC<TextAreaFooterProps> = ({
  children,
  justify = "between",
  className,
  ...rest
}) => {
  const footerClasses = clsx(
    styles.footer,
    styles[`justify-${justify}`],
    className
  );

  return (
    <div className={footerClasses} {...rest}>
      {children}
    </div>
  );
};

TextArea.displayName = "TextArea";

export { TextArea, TextAreaFooter };
export type { TextAreaProps };
